// Unified Review Workflow Integration — pure, identifier-preserving route
// construction and entry-point target resolution.
//
// Every entry point (the `/quarterly-reviews` list rows, the Calendar
// `quarterly_review` event, the customer-detail review control) and every
// repointed legacy affordance targets the Unified_Review_Page using the EXACT
// cycle identifier it was given — never altered, inferred, or substituted
// (Req 1.6, 8.1). These functions are pure and side-effect-free; navigation is
// performed by the callers.
//
// References: design.md "Pure selectors (client/src/lib/reviewWorkflow/)",
// Requirements 1.1, 1.2, 1.3, 1.6, 1.7, 1.8, 8.1.

import type { ReviewCycleRecord } from "./types";

// Re-export the shared review-cycle type contracts so that sibling selectors and
// hooks can import them from this module (Req 1.1). `types.ts` remains the single
// source of truth for the shapes themselves.
export type { CycleStatus, ReviewCycleRecord } from "./types";

/**
 * Build the canonical unified-review route for a cycle identifier.
 *
 * The identifier is embedded verbatim — the function performs no parsing,
 * coercion, inference, or substitution — so callers always reach the exact
 * cycle they referenced (Req 1.6, 8.1).
 */
export function unifiedRouteForCycle(cycleId: number | string): string {
  return `/review-cycles/${cycleId}/unified`;
}

/** Resolution of an entry point that may or may not be openable. */
export type EntryPointTarget =
  | { kind: "navigate"; route: string }
  | { kind: "unopenable" }
  | { kind: "no-active-review" };

/** Result of resolving a Calendar `quarterly_review` event. */
export type CalendarEventTarget =
  | { kind: "navigate"; route: string }
  | { kind: "unopenable" };

/** Result of resolving the customer-detail review control. */
export type CustomerReviewTarget =
  | { kind: "navigate"; route: string }
  | { kind: "no-active-review" };

/**
 * A Calendar event carrying (or failing to carry) a Review_Cycle identifier.
 * The identifier is optional because legacy `quarterly_review` events may omit
 * it entirely (Req 1.8).
 */
export interface CalendarEventLike {
  id?: string | number | null;
}

/**
 * True when a value is a usable Review_Cycle identifier — a finite number or a
 * non-empty, digits-only string. Anything missing, empty, or non-numeric is
 * rejected so no route is ever built from an invalid identifier (Req 1.7, 1.8).
 */
function isValidCycleId(id: unknown): id is number | string {
  if (typeof id === "number") {
    return Number.isFinite(id);
  }
  if (typeof id === "string") {
    const trimmed = id.trim();
    return trimmed.length > 0 && /^\d+$/.test(trimmed);
  }
  return false;
}

/**
 * Resolve the navigation target for a Calendar `quarterly_review` event.
 *
 * Returns a `navigate` target to the carried cycle's unified route when the
 * event carries a valid identifier, and `unopenable` when the identifier is
 * missing, empty, or non-numeric — never producing an invalid route
 * (Req 1.2, 1.8).
 */
export function calendarEventTarget(
  event: CalendarEventLike | null | undefined,
): CalendarEventTarget {
  const id = event?.id;
  if (!isValidCycleId(id)) {
    return { kind: "unopenable" };
  }
  return { kind: "navigate", route: unifiedRouteForCycle(id) };
}

/**
 * Resolve the navigation target for the customer-detail review control.
 *
 * Returns a `navigate` target to the active cycle's unified route when a single
 * active Review_Cycle exists, and `no-active-review` when there is none — never
 * navigating to an invalid identifier (Req 1.3, 1.7).
 */
export function customerReviewTarget(
  activeCycle: ReviewCycleRecord | null | undefined,
): CustomerReviewTarget {
  if (!activeCycle || !isValidCycleId(activeCycle.id)) {
    return { kind: "no-active-review" };
  }
  return { kind: "navigate", route: unifiedRouteForCycle(activeCycle.id) };
}
